import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  Link2,
  ExternalLink,
  FileText,
  Sparkles,
  X,
  Loader2,
  Unlink,
  Trash2,
  CheckCircle2,
  Wallet,
  Pencil,
  Check,
  Lock,
} from "lucide-react";
import { api, formatApiError, getToken } from "@/lib/api";
import { toast } from "sonner";
import SupplierInvoiceHistory from "@/components/SupplierInvoiceHistory";
import SupplierInvoiceRemindersHistory from "@/components/SupplierInvoiceRemindersHistory";
import SupplierInvoiceAuditHistory from "@/components/SupplierInvoiceAuditHistory";
import SupplierInvoiceSourceBadge from "@/components/SupplierInvoiceSourceBadge";
import DuplicateInvoiceModal from "@/components/DuplicateInvoiceModal";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) =>
  new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const shortDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-BE", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS_CLS = {
  received: "border-[#C4553B]/40 bg-[#C4553B]/10 text-[#C4553B]",
  validated: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  paid: "border-emerald-600/40 bg-emerald-500/10 text-emerald-300",
};
const STATUS_LABEL = { received: "Reçue", validated: "Validée", paid: "Payée" };

// Phase 10e — Cellule éditable inline (externalisée pour éviter perte de focus au re-render)
function EditableCell({
  field, value, mono = false, placeholder = "—", testId,
  isEditing, canEdit, editDraft, setEditDraft,
  onStartEdit, onCancelEdit, onSaveEdit, savingEdit,
}) {
  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          className="w-full rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-[#C4553B]"
          value={editDraft || ""}
          onChange={(e) => setEditDraft(e.target.value)}
          autoFocus
          data-testid={`${testId}-input`}
        />
        <button
          onClick={onSaveEdit}
          disabled={savingEdit}
          className="rounded p-1 text-emerald-400 hover:bg-emerald-500/20"
          aria-label="Enregistrer"
          data-testid={`${testId}-save`}
        >
          {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </button>
        <button
          onClick={onCancelEdit}
          disabled={savingEdit}
          className="rounded p-1 text-slate-400 hover:bg-slate-800"
          aria-label="Annuler"
          data-testid={`${testId}-cancel`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  return (
    <div className="group flex items-center gap-1">
      <span className={mono ? "font-mono" : "font-medium"} data-testid={testId}>
        {value || placeholder}
      </span>
      {canEdit ? (
        <button
          onClick={() => onStartEdit(field)}
          className="rounded p-1 text-slate-500 opacity-0 transition group-hover:opacity-100 hover:bg-slate-800 hover:text-slate-200"
          aria-label="Modifier"
          data-testid={`${testId}-edit`}
        >
          <Pencil className="h-3 w-3" />
        </button>
      ) : (
        <Lock className="h-3 w-3 text-slate-600" title="Verrouillé après validation" />
      )}
    </div>
  );
}

// Sous-composant : bloc rattachement (autocomplete + accept/dismiss + detach)
// Phase Rentab-1+2-B (13/09/2026) — MIGRATION `/link-chantier-v2` :
// - `category` OBLIGATOIRE (radio Matériel/Sous-traitance, aucun default)
// - Suggestion visuelle non-bloquante (bordure/label "habituel") jamais un tick auto
// - Toutes les mutations passent désormais par v2. Legacy `/link-chantier` en attach
//   retourne 422 côté backend. Legacy `/accept-suggestion` (sans category) retourne 422.
function LinkChantierSection({ invoice, onChanged }) {
  const [chantiers, setChantiers] = useState([]);
  const [popOpen, setPopOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detachConfirm, setDetachConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  // Phase B — Modal rattachement (v2, category obligatoire)
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkModalMode, setLinkModalMode] = useState("manual"); // "manual" | "accept"
  const [linkModalChantierId, setLinkModalChantierId] = useState(null);
  const [linkModalCategory, setLinkModalCategory] = useState(null); // null = pas de choix
  const [categoryHint, setCategoryHint] = useState(null); // {most_common_category, sample_size}

  useEffect(() => {
    // Charge les chantiers du tenant à l'ouverture du popover (mise en cache locale)
    if (!popOpen || chantiers.length > 0) return;
    setLoading(true);
    api
      .get("/chantiers")
      .then(({ data }) => setChantiers(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [popOpen, chantiers.length]);

  const activeChantiers = useMemo(
    () => chantiers.filter((c) => c.status !== "finalise" && c.status !== "termine"),
    [chantiers]
  );

  // Charge la suggestion visuelle à l'ouverture de la modal (jamais utilisée pour pré-cocher)
  const loadCategoryHint = useCallback(async () => {
    try {
      const { data } = await api.get(`/supplier-invoices/${invoice.id}/category-hint`);
      setCategoryHint(data);
    } catch (e) {
      // Silent — la suggestion est purement UX
      setCategoryHint(null);
    }
  }, [invoice.id]);

  // Ouvre modal en mode "manual" (choix chantier + category)
  const openManualLink = () => {
    setLinkModalMode("manual");
    setLinkModalChantierId(null);
    setLinkModalCategory(null);
    setLinkModalOpen(true);
    // Précharge les chantiers si pas déjà fait
    if (chantiers.length === 0) {
      setLoading(true);
      api.get("/chantiers")
        .then(({ data }) => setChantiers(data || []))
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => setLoading(false));
    }
    loadCategoryHint();
  };

  // Ouvre modal en mode "accept" (suggestion → force choix category)
  const openAcceptSuggestion = () => {
    setLinkModalMode("accept");
    setLinkModalChantierId(invoice.auto_link_suggestion_id);
    setLinkModalCategory(null);
    setLinkModalOpen(true);
    loadCategoryHint();
  };

  const canSubmitLink =
    (linkModalMode === "accept" || !!linkModalChantierId) &&
    (linkModalCategory === "material" || linkModalCategory === "subcontracting");

  const submitLink = async () => {
    if (!canSubmitLink) return;
    setBusy(true);
    try {
      let data;
      if (linkModalMode === "accept") {
        // Phase B — v2 accept avec category obligatoire
        const res = await api.post(
          `/supplier-invoices/${invoice.id}/accept-suggestion-v2`,
          { category: linkModalCategory }
        );
        data = res.data;
      } else {
        // Phase B — v2 link avec chantier_id + category
        const res = await api.post(
          `/supplier-invoices/${invoice.id}/link-chantier-v2`,
          { chantier_id: linkModalChantierId, category: linkModalCategory }
        );
        data = res.data;
      }
      toast.success(
        linkModalCategory === "subcontracting"
          ? "Facture rattachée (catégorie : sous-traitance)."
          : "Facture rattachée (catégorie : matériel)."
      );
      setLinkModalOpen(false);
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doDismiss = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/supplier-invoices/${invoice.id}/dismiss-suggestion`);
      toast.info("Proposition ignorée.");
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doDetach = async () => {
    setBusy(true);
    try {
      // Détachement : la legacy PATCH est encore autorisée pour chantier_id=null
      const { data } = await api.patch(`/supplier-invoices/${invoice.id}/link-chantier`, {
        chantier_id: null,
      });
      toast.success("Chantier détaché — la marge sur achats sera recalculée.");
      setDetachConfirm(false);
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const showSuggestion =
    !invoice.linked_chantier_id &&
    invoice.auto_link_suggestion_id &&
    !invoice.auto_link_dismissed;

  const selectedChantierName = linkModalChantierId
    ? chantiers.find((c) => c.id === linkModalChantierId)?.name ||
      invoice.auto_link_suggestion_name ||
      linkModalChantierId
    : "";
  const hintCat = categoryHint?.most_common_category || null;

  return (
    <div className="rounded border border-slate-800 bg-slate-950/40 p-3" data-testid="detail-link-section">
      <div className="text-xs uppercase tracking-wide text-slate-500">Rattachement chantier</div>

      {/* Proposition auto-détectée */}
      {showSuggestion && (
        <div
          className="mt-2 rounded border border-amber-500/40 bg-amber-500/5 p-3"
          data-testid="detail-suggestion-block"
        >
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 text-amber-400" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wide text-amber-300">
                Proposition auto-détectée
              </div>
              <div className="mt-0.5 text-sm text-slate-100" data-testid="detail-suggestion-name">
                {invoice.auto_link_suggestion_name || invoice.auto_link_suggestion_id}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                Basée sur la référence UBL{" "}
                <span className="font-mono">{invoice.purchase_order_ref || "—"}</span>{" "}
                (confiance :{" "}
                <span className="font-mono">
                  {invoice.auto_link_suggestion_confidence === "strict_uuid"
                    ? "UUID exact"
                    : "motif texte"}
                </span>
                )
              </div>
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  onClick={openAcceptSuggestion}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="detail-suggestion-accept-btn"
                >
                  {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Link2 className="mr-1 h-3 w-3" />}
                  Accepter (choix catégorie requis)
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={doDismiss}
                  disabled={busy}
                  className="border-slate-700 bg-slate-800 text-slate-200"
                  data-testid="detail-suggestion-dismiss-btn"
                >
                  <X className="mr-1 h-3 w-3" /> Ignorer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Etat rattachement */}
      <div className="mt-2 flex items-center justify-between">
        <div className="text-sm">
          {invoice.linked_chantier_id ? (
            <span data-testid="detail-linked-chantier" className="inline-flex items-center gap-2">
              <Link2 className="h-3 w-3 text-emerald-400" />
              <span className="font-medium">
                {invoice.linked_chantier_name || invoice.linked_chantier_id}
              </span>
              {invoice.category && (
                <Badge
                  className={
                    invoice.category === "subcontracting"
                      ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                      : "border-slate-600/50 bg-slate-700/20 text-slate-300"
                  }
                  data-testid="detail-linked-category-badge"
                >
                  {invoice.category === "subcontracting" ? "Sous-traitance" : "Matériel"}
                </Badge>
              )}
            </span>
          ) : (
            <span className="text-slate-500">Aucun chantier rattaché</span>
          )}
        </div>
        <div className="flex gap-2">
          {invoice.linked_chantier_id ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDetachConfirm(true)}
              disabled={busy}
              className="border-slate-700 bg-slate-800 text-slate-200 hover:text-red-400"
              data-testid="detail-detach-btn"
            >
              <Unlink className="mr-1 h-3 w-3" /> Détacher
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={openManualLink}
              disabled={busy}
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="detail-link-chantier-btn"
            >
              <Link2 className="mr-1 h-3 w-3" /> Rattacher à un chantier
            </Button>
          )}
        </div>
      </div>

      {/* Modal rattachement — v2 avec category OBLIGATOIRE */}
      <Dialog open={linkModalOpen} onOpenChange={setLinkModalOpen}>
        <DialogContent
          className="max-w-md border-slate-800 bg-slate-900 text-slate-100"
          data-testid="detail-link-modal"
        >
          <DialogHeader>
            <DialogTitle>
              {linkModalMode === "accept"
                ? "Accepter la proposition"
                : "Rattacher à un chantier"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {linkModalMode === "accept" ? (
                <>
                  Chantier proposé :{" "}
                  <span className="font-medium text-slate-200">
                    {selectedChantierName}
                  </span>
                  . Choisis la catégorie pour finaliser.
                </>
              ) : (
                <>Sélectionne un chantier puis une catégorie (choix obligatoire).</>
              )}
            </DialogDescription>
          </DialogHeader>

          {linkModalMode === "manual" && (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wide text-slate-500">
                Chantier
              </label>
              {/* Autocomplete via Command */}
              <div
                className="rounded border border-slate-700 bg-slate-950/50 p-0 text-slate-100"
                data-testid="link-modal-chantier-picker"
              >
                <Command className="bg-slate-950/50 text-slate-100">
                  <CommandInput
                    placeholder="Rechercher un chantier…"
                    className="text-slate-100"
                    data-testid="link-modal-chantier-search"
                  />
                  <CommandList>
                    {loading ? (
                      <div className="p-3 text-xs text-slate-500">Chargement…</div>
                    ) : (
                      <>
                        <CommandEmpty>Aucun chantier trouvé.</CommandEmpty>
                        <CommandGroup heading="Chantiers actifs">
                          {activeChantiers.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={`${c.name} ${c.reference_code || ""}`}
                              onSelect={() => setLinkModalChantierId(c.id)}
                              className={`cursor-pointer ${
                                linkModalChantierId === c.id ? "bg-slate-800/60" : ""
                              }`}
                              data-testid={`link-modal-option-${c.id}`}
                            >
                              <div className="flex flex-col text-sm">
                                <span className="font-medium">{c.name}</span>
                                {c.reference_code && (
                                  <span className="text-xs font-mono text-slate-500">
                                    {c.reference_code}
                                  </span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </div>
              {linkModalChantierId && (
                <div className="text-xs text-slate-400" data-testid="link-modal-chantier-selected">
                  Sélectionné :{" "}
                  <span className="font-medium text-slate-200">{selectedChantierName}</span>
                </div>
              )}
            </div>
          )}

          {/* Radio catégorie — OBLIGATOIRE, aucun default coché */}
          <div className="space-y-2" data-testid="link-modal-category-group">
            <label className="text-xs uppercase tracking-wide text-slate-500">
              Catégorie (choix obligatoire)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "material", label: "Matériel" },
                { value: "subcontracting", label: "Sous-traitance" },
              ].map((opt) => {
                const isHint = hintCat === opt.value;
                const isSelected = linkModalCategory === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`cursor-pointer rounded border p-3 text-sm transition ${
                      isSelected
                        ? "border-[#C4553B] bg-[#C4553B]/10 text-[#C4553B]"
                        : isHint
                        ? "border-amber-500/60 bg-amber-500/5 text-amber-100"
                        : "border-slate-700 bg-slate-950/40 text-slate-200 hover:border-slate-600"
                    }`}
                    data-testid={`link-modal-category-${opt.value}`}
                  >
                    <input
                      type="radio"
                      name="link-modal-category"
                      value={opt.value}
                      checked={isSelected}
                      onChange={() => setLinkModalCategory(opt.value)}
                      className="mr-2 accent-[#C4553B]"
                      data-testid={`link-modal-category-${opt.value}-input`}
                    />
                    <span className="font-medium">{opt.label}</span>
                    {isHint && !isSelected && (
                      <span
                        className="ml-1 text-[10px] text-amber-300"
                        data-testid={`link-modal-category-${opt.value}-hint`}
                      >
                        (habituel)
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500">
              Décision comptable requise. Aucun choix par défaut — le calcul de
              marge dépend de cette catégorie.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLinkModalOpen(false)}
              disabled={busy}
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="link-modal-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              size="sm"
              onClick={submitLink}
              disabled={busy || !canSubmitLink}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C] disabled:opacity-40"
              data-testid="link-modal-submit-btn"
            >
              {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Link2 className="mr-1 h-3 w-3" />}
              Rattacher
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation détachement */}
      <AlertDialog open={detachConfirm} onOpenChange={setDetachConfirm}>
        <AlertDialogContent
          className="border-slate-800 bg-slate-900 text-slate-100"
          data-testid="detail-detach-confirm-modal"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Détacher cette facture ?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              La facture ne sera plus rattachée au chantier{" "}
              <span className="font-medium text-slate-200">
                {invoice.linked_chantier_name || invoice.linked_chantier_id}
              </span>
              . La <b>marge sur achats</b> de ce chantier sera automatiquement
              recalculée (retrait de {money(invoice.amount_htva)} HTVA).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="detail-detach-cancel"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDetach}
              disabled={busy}
              className="bg-red-600 text-white hover:bg-red-500"
              data-testid="detail-detach-confirm"
            >
              {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Détacher
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function SupplierInvoiceDetail({ open, onOpenChange, invoice: invoiceProp, onChanged }) {
  const { state } = useAuth();
  const currentRole = state?.user?.role;
  const canWorkflow = currentRole === "admin" || currentRole === "secretaire";
  const [invoice, setInvoice] = useState(invoiceProp);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidRef, setMarkPaidRef] = useState("");
  const [markingPaid, setMarkingPaid] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [duplicateConflict, setDuplicateConflict] = useState(null);
  useEffect(() => setInvoice(invoiceProp), [invoiceProp]);
  if (!invoice) return null;

  const canEdit = canWorkflow && (invoice.status === "received" || !invoice.status);
  const canDelete =
    (invoice.status === "received" || !invoice.status) && !invoice.linked_chantier_id && currentRole === "admin";
  const canValidate = canWorkflow && (invoice.status === "received" || !invoice.status);
  const canMarkPaid = canWorkflow && invoice.status === "validated";

  const dl = (kind) => {
    // C1A v2 (15/09/2026) — Utilise getToken() (sessionStorage) au lieu de
    // la prop `authToken` (qui pointait sur une clé legacy `werko_token`
    // jamais écrite → Authorization: Bearer null → échec silencieux).
    // Ajout d'un `.catch()` visible : plus d'échec muet côté utilisateur.
    const url = `${API}/api/supplier-invoices/${invoice.id}/${kind}`;
    const token = getToken();
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${invoice.invoice_number || invoice.id}.${kind}`;
        link.click();
      })
      .catch(async (r) => {
        let detail = "";
        try {
          if (r && typeof r.json === "function") {
            const body = await r.json();
            detail = body?.detail || "";
          }
        } catch {
          /* ignore */
        }
        const status = r?.status ? ` (HTTP ${r.status})` : "";
        const msg = detail
          ? `Téléchargement impossible : ${detail}${status}`
          : `Téléchargement du ${kind.toUpperCase()} impossible${status}`;
        toast.error(msg);
      });
  };

  const handleChanged = (updated) => {
    setInvoice(updated);
    if (onChanged) onChanged();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/supplier-invoices/${invoice.id}`);
      toast.success("Facture supprimée définitivement.");
      setDeleteConfirm(false);
      onOpenChange(false);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const { data } = await api.post(`/supplier-invoices/${invoice.id}/validate`);
      toast.success("Facture validée.");
      handleChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setValidating(false);
    }
  };

  const handleMarkPaid = async () => {
    setMarkingPaid(true);
    try {
      const { data } = await api.post(`/supplier-invoices/${invoice.id}/mark-paid`, {
        payment_reference: markPaidRef.trim() || undefined,
      });
      toast.success("Facture marquée payée.");
      setMarkPaidOpen(false);
      setMarkPaidRef("");
      handleChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMarkingPaid(false);
    }
  };

  // Phase 10e — Édition inline d'un champ (tant que status == received)
  const startEdit = (field) => {
    setEditingField(field);
    setEditDraft(invoice[field] ?? "");
  };
  const cancelEdit = () => {
    setEditingField(null);
    setEditDraft("");
  };
  const saveEdit = async () => {
    if (!editingField) return;
    setSavingEdit(true);
    try {
      const payload = { [editingField]: editDraft === "" ? null : editDraft };
      const { data } = await api.patch(`/supplier-invoices/${invoice.id}`, payload);
      toast.success("Champ mis à jour.");
      cancelEdit();
      handleChanged(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409 && typeof e?.response?.data?.detail === "object") {
        setDuplicateConflict(e.response.data.detail);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSavingEdit(false);
    }
  };

  // Composant helper wrapping avec les props communs du composant parent
  const renderCell = (field, value, opts = {}) => (
    <EditableCell
      field={field}
      value={value}
      mono={opts.mono}
      placeholder={opts.placeholder}
      testId={opts.testId}
      isEditing={editingField === field}
      canEdit={canEdit}
      editDraft={editDraft}
      setEditDraft={setEditDraft}
      onStartEdit={startEdit}
      onCancelEdit={cancelEdit}
      onSaveEdit={saveEdit}
      savingEdit={savingEdit}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-slate-800 bg-slate-900 text-slate-100" data-testid="supplier-invoice-detail">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3">
            <FileText className="h-5 w-5 text-[#C4553B]" />
            {invoice.supplier_name}
            <SupplierInvoiceSourceBadge source={invoice.source} />
            <Badge className={`border ${STATUS_CLS[invoice.status] || STATUS_CLS.received}`}>
              {STATUS_LABEL[invoice.status] || invoice.status}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            N° {invoice.invoice_number} · Émise {shortDate(invoice.issue_date)} · Échéance {shortDate(invoice.due_date)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Fournisseur */}
          <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Fournisseur</div>
            <div className="mt-1 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-slate-500 text-xs">Nom</div>
                {renderCell("supplier_name", invoice.supplier_name, { testId: "detail-supplier-name" })}
              </div>
              <div>
                <div className="text-slate-500 text-xs">TVA</div>
                {renderCell("supplier_vat", invoice.supplier_vat, { mono: true, testId: "detail-supplier-vat" })}
              </div>
              <div className="col-span-2">
                <div className="text-slate-500 text-xs">IBAN</div>
                {renderCell("supplier_iban", invoice.supplier_iban, { mono: true, testId: "detail-supplier-iban" })}
              </div>
            </div>
          </div>

          {/* Totaux */}
          <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Montants</div>
            <div className="mt-1 grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-slate-500 text-xs">HTVA</div>
                <div className="tabular-nums" data-testid="detail-amount-htva">{money(invoice.amount_htva)}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">TVA</div>
                <div className="tabular-nums" data-testid="detail-amount-vat">{money(invoice.amount_vat)}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">TVAC</div>
                <div className="font-semibold text-lg tabular-nums" data-testid="detail-amount-tvac">{money(invoice.amount_tvac)}</div>
              </div>
            </div>
            {invoice.vat_breakdown?.length > 0 && (
              <table className="mt-3 w-full text-xs" data-testid="detail-vat-breakdown">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left p-1">Taux</th>
                    <th className="text-right p-1">Base HTVA</th>
                    <th className="text-right p-1">TVA</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.vat_breakdown.map((v, i) => (
                    <tr key={i} className="border-t border-slate-800">
                      <td className="p-1">{v.rate}%</td>
                      <td className="p-1 text-right tabular-nums">{money(v.base_htva)}</td>
                      <td className="p-1 text-right tabular-nums">{money(v.vat_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Lignes */}
          {invoice.line_items?.length > 0 && (
            <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Lignes</div>
              <table className="w-full text-xs" data-testid="detail-line-items">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left p-1">Description</th>
                    <th className="text-right p-1">Qté</th>
                    <th className="text-right p-1">PU</th>
                    <th className="text-right p-1">Total HT</th>
                    <th className="text-right p-1">TVA</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((l, i) => (
                    <tr key={i} className="border-t border-slate-800">
                      <td className="p-1">{l.description}</td>
                      <td className="p-1 text-right tabular-nums">{l.quantity}</td>
                      <td className="p-1 text-right tabular-nums">{money(l.unit_price)}</td>
                      <td className="p-1 text-right tabular-nums">{money(l.total_htva)}</td>
                      <td className="p-1 text-right">{l.vat_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Références */}
          <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Références</div>
            <div className="mt-1 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-slate-500 text-xs">Bon de commande</div>
                {renderCell("purchase_order_ref", invoice.purchase_order_ref, { mono: true, testId: "detail-po-ref" })}
              </div>
              <div>
                <div className="text-slate-500 text-xs">Communication paiement</div>
                {renderCell("payment_reference", invoice.payment_reference, { mono: true, testId: "detail-payment-ref" })}
              </div>
            </div>
          </div>

          {/* Phase 10e — Notes internes (visible admin/secrétaire uniquement, jamais client) */}
          {(invoice.internal_notes || canEdit) && (
            <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Notes internes</div>
              {renderCell("internal_notes", invoice.internal_notes, { testId: "detail-internal-notes" })}
            </div>
          )}

          {/* Phase 10b — Rattachement chantier activé */}
          <LinkChantierSection invoice={invoice} onChanged={handleChanged} />

          {/* Phase 10c — Historique action (mini timeline) */}
          <SupplierInvoiceHistory invoice={invoice} />

          {/* Phase 10e — Historique éditions (masqué si aucun) */}
          <SupplierInvoiceAuditHistory invoice={invoice} />

          {/* Phase 10d — Historique rappels échéance (masqué si aucun rappel) */}
          <SupplierInvoiceRemindersHistory invoice={invoice} />

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => dl("ubl")}
              variant="outline"
              size="sm"
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="detail-download-ubl"
            >
              <Download className="mr-1 h-3.5 w-3.5" /> UBL XML
            </Button>
            <Button
              onClick={() => dl("pdf")}
              disabled={!invoice.has_pdf}
              variant="outline"
              size="sm"
              className="border-slate-700 bg-slate-800 text-slate-200 disabled:opacity-40"
              data-testid="detail-download-pdf"
            >
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> PDF {invoice.has_pdf ? "" : "(non disponible)"}
            </Button>
            {canValidate && (
              <Button
                onClick={handleValidate}
                disabled={validating}
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-500"
                data-testid="detail-validate-btn"
              >
                {validating ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
                Valider
              </Button>
            )}
            {canMarkPaid && (
              <Button
                onClick={() => setMarkPaidOpen(true)}
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-500"
                data-testid="detail-mark-paid-btn"
              >
                <Wallet className="mr-1 h-3.5 w-3.5" /> Marquer payée
              </Button>
            )}
            {canDelete && (
              <Button
                onClick={() => setDeleteConfirm(true)}
                variant="outline"
                size="sm"
                className="ml-auto border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-600 hover:text-white"
                data-testid="detail-delete-btn"
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Supprimer
              </Button>
            )}
          </div>
        </div>

        {/* Modal Marquer payée (input payment_reference optionnel — pas de checkbox notify car flux interne) */}
        <AlertDialog open={markPaidOpen} onOpenChange={setMarkPaidOpen}>
          <AlertDialogContent
            className="border-slate-800 bg-slate-900 text-slate-100"
            data-testid="detail-mark-paid-modal"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Marquer payée — {invoice.supplier_name}</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Confirmer le paiement de la facture{" "}
                <span className="font-mono text-slate-200">N°{invoice.invoice_number}</span>{" "}
                pour <b className="text-slate-200">{money(invoice.amount_tvac)}</b> TVAC.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <label htmlFor="mp-ref" className="text-xs uppercase tracking-wide text-slate-500">
                Référence virement (optionnel)
              </label>
              <input
                id="mp-ref"
                value={markPaidRef}
                onChange={(e) => setMarkPaidRef(e.target.value)}
                placeholder="Ex : Virement Belfius du 15/03"
                className="w-full rounded border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="detail-payment-ref-input"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="border-slate-700 bg-slate-800 text-slate-200"
                data-testid="detail-mark-paid-cancel"
              >
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="bg-emerald-600 text-white hover:bg-emerald-500"
                data-testid="detail-mark-paid-confirm"
              >
                {markingPaid ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                Confirmer le paiement
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirmation suppression */}
        <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
          <AlertDialogContent
            className="border-slate-800 bg-slate-900 text-slate-100"
            data-testid="detail-delete-confirm-modal"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer définitivement la facture ?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Supprimer définitivement la facture{" "}
                <span className="font-mono text-slate-200">N°{invoice.invoice_number}</span> de{" "}
                <span className="font-medium text-slate-200">{invoice.supplier_name}</span> ?
                <br />
                <span className="mt-2 block text-red-400/90 text-xs">
                  Cette action est irréversible. Seule une trace anonymisée restera dans l&apos;audit log.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="border-slate-700 bg-slate-800 text-slate-200"
                data-testid="detail-delete-cancel"
              >
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 text-white hover:bg-red-500"
                data-testid="detail-delete-confirm"
              >
                {deleting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                Supprimer définitivement
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Phase 10e — Modal duplicate 409 (édition inline peut le déclencher) */}
        <DuplicateInvoiceModal
          open={!!duplicateConflict}
          onOpenChange={(o) => !o && setDuplicateConflict(null)}
          conflict={duplicateConflict}
          onViewExisting={() => setDuplicateConflict(null)}
        />
      </DialogContent>
    </Dialog>
  );
}
